/**
 * API для загрузки настроек чата из Supabase
 */

import { chatLogger } from '@/utils/chatLogger';
import type { SupportedLanguage } from '@/types/config.types';

export interface ChatSettingsContacts {
  phone: string;
  phone_link: string;
  whatsapp: string;
  telegram: string;
  email: string;
  website: string;
}

export interface QuickQuestion {
  id: string;
  text_ru: string;
  text_en: string;
  text_de: string;
}

export interface ChatSettings {
  id: string;
  api_endpoint: string;
  api_key: string | null;
  contacts: ChatSettingsContacts;       // RU
  contacts_en: ChatSettingsContacts;    // EN
  contacts_de: ChatSettingsContacts;    // DE
  domain_language_map?: Record<string, string>;
  callback_endpoint?: string;
  chat_endpoint?: string;
  quick_questions: QuickQuestion[];
  welcome_message_ru: string;
  welcome_message_en: string;
  welcome_message_de: string;
  fallback_message_ru: string;
  fallback_message_en: string;
  fallback_message_de: string;
  is_active: boolean;
  show_contacts: boolean;
  show_quick_questions: boolean;
  enable_file_upload: boolean;
  translations?: Record<string, Record<string, string>>;
}

/**
 * Загружает настройки чата из Supabase (public endpoint)
 */
export async function fetchChatSettings(supabaseUrl: string, supabaseAnonKey: string): Promise<ChatSettings | null> {
  try {
    const response = await fetch(
      `${supabaseUrl}/rest/v1/chat_settings?is_active=eq.true&limit=1`,
      {
        headers: {
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
      }
    );

    if (!response.ok) {
      chatLogger.error('[Settings API] Failed to fetch settings:', response.statusText);
      return null;
    }

    const data = await response.json();
    return data[0] || null;
  } catch (error) {
    chatLogger.error('[Settings API] Error fetching settings:', error);
    return null;
  }
}

/**
 * Получить контакты для языка
 */
export function getContactsForLanguage(settings: ChatSettings, lang: string): ChatSettingsContacts {
  switch (lang) {
    case 'en':
      return settings.contacts_en || settings.contacts;
    case 'de':
      return settings.contacts_de || settings.contacts;
    default:
      return settings.contacts;
  }
}

/**
 * Получить текст быстрого вопроса на нужном языке
 */
export function getQuestionText(question: QuickQuestion, lang: string): string {
  switch (lang) {
    case 'en':
      return question.text_en;
    case 'de':
      return question.text_de;
    default:
      return question.text_ru;
  }
}

/**
 * Получить приветственное сообщение на нужном языке
 */
export function getWelcomeMessage(settings: ChatSettings, lang: string): string {
  switch (lang) {
    case 'en':
      return settings.welcome_message_en;
    case 'de':
      return settings.welcome_message_de;
    default:
      return settings.welcome_message_ru;
  }
}

/**
 * Определяет язык по домену/пути на основе domain_language_map
 * Поддерживает ключи:
 * - "larte-design.com" (по host)
 * - "larte-design.com/de" (по host+prefix path)
 */
export function resolveLanguageFromLocation(
  settings: ChatSettings,
  location: Location,
  fallback: string
): string {
  const map = settings.domain_language_map || {};
  const host = location.hostname;
  const path = location.pathname || '/';

  // 1) Самый специфичный: host + path-prefix (например /de)
  const candidates = Object.keys(map)
    .filter((k) => k.startsWith(host + '/'))
    .sort((a, b) => b.length - a.length);

  for (const key of candidates) {
    const prefix = key.substring(host.length); // "/de"
    if (path.startsWith(prefix)) return map[key] || fallback;
  }

  // 2) Только host
  if (map[host]) return map[host];

  return fallback;
}

/**
 * Преобразовать QuickQuestion[] в SuggestedQuestion[] для компонента
 */
export function mapQuickQuestions(questions: QuickQuestion[], lang: string) {
  return questions.map(q => ({
    id: q.id,
    text: getQuestionText(q, lang),
  }));
}

/**
 * Получить fallback сообщение по языку
 */
export function getFallbackMessage(settings: ChatSettings, lang: string): string {
  switch (lang) {
    case 'en':
      return settings.fallback_message_en;
    case 'de':
      return settings.fallback_message_de;
    default:
      return settings.fallback_message_ru;
  }
}

export function isSupportedLanguage(value: string | undefined | null): value is SupportedLanguage {
  return value === 'ru' || value === 'en' || value === 'de' || value === 'fr';
}
